import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetNewsArticleSlugs, getServerPageGetNewsDetail } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	BodyTextFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	NewsArticleFieldsFragment,
	NewsDetailSingleFieldsFragment,
	PageInformationFieldsFragment,
	Tag
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';
import { BaseModule } from 'modules/base-module/base-module';
import { Head } from 'modules/head/head';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface INewsDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	newsDetail?: NewsDetailSingleFieldsFragment;
	newsarticleBySlug?: NewsArticleFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const NewsDetailPage = (props: INewsDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.newsarticleBySlug || null,
		pageQuery: () => getServerPageGetNewsDetail({ variables: props.variables }, client)
	});

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.newsarticleBySlug?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'Newsarticle' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page?.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
						}}
					>
						<IntroHeader
							module={{
								title: page.header?.title || '',
								backgroundColor: page.header?.backgroundColor,
								excerpt: page.header?.excerpt || '',
								cover: page.header?.cover,
								tags: (page.tags as Tag[]) || []
							}}
							slug={page.slug}
						/>
					</BaseModule>
					<BaseModule defaultModuleOptions={page.bodyText?.defaultModuleOptions}>
						<BodyText module={page.bodyText as BodyTextFieldsFragment} publishedAt={page.firstPublishedAt} />
					</BaseModule>
					<ModuleBuilder modules={page.modules || []} slug={page.slug} />
					<ModuleBuilder modules={props?.newsDetail?.modules || []} slug={page.slug} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetNewsArticleSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.NEWS.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.NEWS.slug}/${ctx.params.slug}` : '/';
		const variables = { slug, locale, publicationState };

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::newsarticle.newsarticle',
			contentTypeName: 'newsarticle',
			fetchPage: () => getServerPageGetNewsDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.newsarticleBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		let newsDetail = null;
		if (!passwordResult.isPasswordProtected && passwordResult.page) {
			const detail = await getServerPageGetNewsDetail({ variables }, client);
			newsDetail = detail.props.data?.newsDetail?.data?.attributes || null;
		}

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				newsDetail: newsDetail,
				newsarticleBySlug: passwordResult.page,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in news detail', error);
		return { notFound: true };
	}
};

export default NewsDetailPage;
